import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Avatar,

  TextField,
  IconButton,
  Card,
  CardContent,
  Fade,
  useTheme,
  Grid,
  Button,
  keyframes,
  Chip,
  LinearProgress,
  Tooltip,
  alpha,
  Slide,
  Zoom,
  useMediaQuery,
} from "@mui/material";
import {
  Favorite as FavoriteIcon,
  Close as CloseIcon,
 
  Balance as BalanceIcon,
  CameraAlt as CameraAltIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CompareArrows as CompareArrowsIcon,
  EmojiEvents as TrophyIcon,
  Gamepad as GamepadIcon,
  ShoppingCart as ShoppingCartIcon,
  Verified as VerifiedIcon,
  

  Psychology as PsychologyIcon,
 
} from "@mui/icons-material";
import Swal from "sweetalert2";
import CountryFlag from "./CountryFlag";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";

import NotEnoughCoinsDialog from "./NotEnoughCoinsDialog";
import { useUser } from "../authMethods/UserContext";

// Enhanced keyframes for animations
const coinSpin = keyframes`
  0% { transform: rotateY(0deg) scale(1); }
  50% { transform: rotateY(180deg) scale(1.1); }
  100% { transform: rotateY(360deg) scale(1); }
`;

const winRatePulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

const winRateProgress = (winRate) => keyframes`
  0% { 
    background: conic-gradient(from 0deg, #e0e0e0 0deg 360deg);
  }
  100% { 
    background: conic-gradient(
      from 0deg, 
      ${winRate > 0 ? '#4caf50' : '#e0e0e0'} 0deg ${(winRate / 100) * 360}deg, 
      #e0e0e0 ${(winRate / 100) * 360}deg 360deg
    );
  }
`;

const Profile = ({ open, onClose, avatar }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [avatarUrl, setAvatarUrl] = useState(avatar?.url || "");
  const [avatarName, setAvatarName] = useState(avatar?.name || "");
  const [editingName, setEditingName] = useState(false);
  const { userData } = useUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMyUser, setIsMyUser] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [showStats, setShowStats] = useState(false);

  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setShowStats(true), 300);
      return () => clearTimeout(timer);
    } else {
      setShowStats(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (userData) {
      setAvatarUrl(userData.url);
      setAvatarName(userData.name);
    }
  }, [userData]);

  React.useEffect(() => {
    if (avatar) {
      const isMyProfile = avatar?.id === userData?.id;
      setIsMyUser(isMyProfile);
      setAvatarUrl(userData?.url);

      if (!isMyProfile && avatar?.settings?.allowOthersToSeeMyProfile === false) {
        Swal.fire({
          icon: "warning",
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonColor: "#d33",
          text: t("You can't view this profile because the player has disabled public access"),
          cancelButtonText: t("Cancel"),
        });
        onClose();
        return;
      }

      setAvatarUrl(avatar.url);
      setAvatarName(avatar.name);
    }
  }, [avatar, userData?.id, userData?.url, t, onClose]);

  const handleAddToFavorite = () => {
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "add_to_favorite";
    generalMessage.user.userName = avatar.userName;
    sendMessage(generalMessage);
  };

  const handleUpdateName = async () => {
    try {
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "user_data_update";
      generalMessage.user.name = avatarName;
      sendMessage(generalMessage);
    } catch (error) {
      console.error("Error updating name:", error);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "DemocraticChess");
    formData.append("folder", "Avatars");

    try {
      const res = await fetch("https://api.cloudinary.com/v1_1/dnlgtek2a/image/upload", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      setAvatarUrl(data.secure_url);

      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "user_data_update";
      generalMessage.user.url = data.secure_url;
      sendMessage(generalMessage);
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!avatarUrl) return;

    try {
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "user_data_update";
      generalMessage.user.url = "remove";
      sendMessage(generalMessage);
      setAvatarUrl("");
    } catch (error) {
      console.error("Error deleting avatar:", error);
    }
  };

  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleBuyCoins = () => setIsDialogOpen(false);
  const handleBuyCoinsClick = () => setIsDialogOpen(true);

  const handleAddOrRemove = () => {
    if (userData?.favorites?.some(fav => fav === avatar?.id)) {
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "remove_from_favorite";
      generalMessage.user.favorites = [];
      generalMessage.user.favorites.push(avatar.id);
      sendMessage(generalMessage);
    } else {
      handleAddToFavorite();
    }
  };

  // Statistics calculations
  const totalGames = avatar?.userStatistics?.totalGames || 0;
  const totalWins = avatar?.userStatistics?.totalWins || 0;
  const totalLose = avatar?.userStatistics?.totalLose || 0;
  const totalDraw = avatar?.userStatistics?.totalDraw || 0;
  const winRate = totalGames > 0 ? Math.round((totalWins / totalGames) * 10000) : 0;
  const coins = avatar?.userStatistics?.coins || 0;
  const winPercentage = totalGames > 0 ? ((totalWins / totalGames) * 100) : 0;

  // Animation duration based on win rate
  const animationDuration = Math.max((winPercentage / 100) * 3, 0.5);

  // Effect to restart animation
  React.useEffect(() => {
    if (totalGames > 0 && showStats) {
      const interval = setInterval(() => {
        setAnimationKey(prev => prev + 1);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [totalGames, showStats]);

  // Enhanced StatCard component - Mobile optimized
  const StatCard = ({ icon, label, value, color = "primary", progress, subtitle }) => (
    <Fade in={showStats} timeout={600}>
      <Card
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)}, ${alpha(theme.palette[color].main, 0.05)})`,
          border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`,
          borderRadius: { xs: 2, sm: 3 },
          height: "100%",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: isMobile ? "none" : "translateY(-4px)",
            boxShadow: `0 ${isMobile ? 4 : 8}px ${isMobile ? 15 : 25}px ${alpha(theme.palette[color].main, 0.3)}`,
            border: `2px solid ${theme.palette[color].main}`,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1, sm: 2 }, height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 0.5, sm: 1 } }}>
            <Avatar
              sx={{
                width: { xs: 24, sm: 32 },
                height: { xs: 24, sm: 32 },
                background: `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
                mr: { xs: 1, sm: 1.5 },
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: { xs: 14, sm: 18 } } })}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  fontSize: { xs: "1.1rem", sm: "1.5rem" },
                  lineHeight: 1,
                }}
              >
                {typeof value === 'number' ? value.toLocaleString() : value}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontSize: { xs: "0.6rem", sm: "0.75rem" },
                    display: { xs: "none", sm: "block" }
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {progress !== undefined && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: { xs: 3, sm: 4 },
                borderRadius: 2,
                backgroundColor: alpha(theme.palette[color].main, 0.2),
                "& .MuiLinearProgress-bar": {
                  background: `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
                  borderRadius: 2,
                },
              }}
            />
          )}
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        zIndex: 3000,
        "& .MuiDialog-paper": {
          borderRadius: isMobile ? 0 : 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.02)})`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          maxHeight: "95vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Enhanced Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "white",
          position: "relative",
          p: 0,
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='m0 20l20-20h-20v20z'/%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.3,
          },
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={avatarUrl || userData?.url}
                  alt={avatarName || "Avatar"}
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    border: { xs: "3px solid white", sm: "4px solid white" },
                    boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
                  }}
                />
                {isMyUser && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: { xs: -6, sm: -8 },
                      right: { xs: -6, sm: -8 },
                      display: "flex",
                      gap: 0.5,
                    }}
                  >
                    <input
                      accept="image/*"
                      type="file"
                      onChange={handleAvatarUpload}
                      style={{ display: "none" }}
                      id="avatar-upload"
                    />
                    <label htmlFor="avatar-upload">
                      <Tooltip title={t("Upload Avatar")}>
                        <IconButton
                          component="span"
                          size="small"
                          sx={{
                            bgcolor: "white",
                            color: theme.palette.primary.main,
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                            "&:hover": { 
                              bgcolor: "rgba(255,255,255,0.9)",
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          <CameraAltIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                        </IconButton>
                      </Tooltip>
                    </label>
                    {avatarUrl && (
                      <Tooltip title={t("Remove Avatar")}>
                        <IconButton
                          onClick={handleRemoveAvatar}
                          size="small"
                          sx={{
                            bgcolor: "#f44336",
                            color: "white",
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                            "&:hover": { 
                              bgcolor: "#d32f2f",
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>

              <Box>
                {editingName && isMyUser ? (
                  <TextField
                    value={avatarName}
                    onChange={(e) => setAvatarName(e.target.value)}
                    onBlur={() => {
                      setEditingName(false);
                      handleUpdateName();
                    }}
                    autoFocus
                    variant="standard"
                    sx={{
                      "& .MuiInput-input": {
                        color: "white",
                        fontSize: { xs: "1.2rem", sm: "1.5rem" },
                        fontWeight: 700,
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "rgba(255,255,255,0.5)",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "white",
                      },
                    }}
                  />
                ) : (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant={isMobile ? "h6" : "h5"}
                        onClick={() => isMyUser && setEditingName(true)}
                        sx={{
                          cursor: isMyUser ? "pointer" : "default",
                          fontWeight: 700,
                          textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          fontSize: { xs: "1.2rem", sm: "1.5rem" },
                        }}
                      >
                        {avatarName || t("Choose your player name")}
                      </Typography>
                      {isMyUser && (
                        <Tooltip title={t("Edit Name")}>
                          <IconButton
                            onClick={() => setEditingName(true)}
                            size="small"
                            sx={{ 
                              color: "rgba(255,255,255,0.8)",
                              "&:hover": { color: "white", transform: "scale(1.1)" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      {avatar?.countryCode && (
                        <Box sx={{ transform: { xs: "scale(0.8)", sm: "scale(1)" } }}>
                          <CountryFlag countryCode={avatar?.countryCode} />
                        </Box>
                      )}
                      <Chip
                        icon={<VerifiedIcon />}
                        label={isMyUser ? t("Your Profile") : t("Player Profile")}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "white",
                          fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          height: { xs: 24, sm: 32 },
                          "& .MuiChip-icon": { 
                            color: "white",
                            fontSize: { xs: 14, sm: 16 }
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <IconButton
              onClick={onClose}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.2)",
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                "&:hover": { 
                  bgcolor: "rgba(255,255,255,0.3)",
                  transform: "scale(1.1)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, maxHeight: "70vh", overflowY: "auto" }}>
        <NotEnoughCoinsDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onBuyCoins={handleBuyCoins}
          buy={true}
          userData={userData}
        />

        {avatar?.userType !== "AI" ? (
          <Box>
            {/* Coins Section */}
            {isMyUser && (
              <Slide direction="down" in={showStats} timeout={400}>
                <Card
                  elevation={0}
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    background: "linear-gradient(135deg, #FFD700, #FFA000)",
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
                        <Box
                          component="img"
                          src="https://res.cloudinary.com/dnlgtek2a/image/upload/v1743405234/Pictures/coin.png"
                          alt="Coins"
                          sx={{ 
                            width: { xs: 36, sm: 48 }, 
                            height: { xs: 36, sm: 48 },
                            animation: `${coinSpin} 3s linear infinite`,
                            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                          }}
                        />
                        <Box>
                          <Typography 
                            variant={isMobile ? "body1" : "h6"}
                            sx={{ 
                              color: "white", 
                              fontWeight: 700,
                              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            }}
                          >
                            {t("Total Coins")}
                          </Typography>
                          <Typography 
                            variant={isMobile ? "h5" : "h3"}
                            sx={{ 
                              fontWeight: 900, 
                              color: "white", 
                              lineHeight: 1,
                              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            }}
                          >
                            {coins.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        onClick={handleBuyCoinsClick}
                        variant="contained"
                        startIcon={<ShoppingCartIcon />}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "white",
                          fontWeight: 700,
                          backdropFilter: "blur(10px)",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderRadius: { xs: 2, sm: 3 },
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.5 },
                          fontSize: { xs: "0.8rem", sm: "1rem" },
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.3)",
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        {t("Buy More")}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Slide>
            )}

            {/* Statistics Grid */}
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<TrophyIcon />}
                  label={t("Wins")}
                  value={totalWins}
                  color="success"
                  progress={totalGames > 0 ? (totalWins / totalGames) * 100 : 0}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<CloseIcon />}
                  label={t("Losses")}
                  value={totalLose}
                  color="error"
                  progress={totalGames > 0 ? (totalLose / totalGames) * 100 : 0}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<BalanceIcon />}
                  label={t("Draws")}
                  value={totalDraw}
                  color="warning"
                  progress={totalGames > 0 ? (totalDraw / totalGames) * 100 : 0}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  icon={<GamepadIcon />}
                  label={t("Total Games")}
                  value={totalGames}
                  color="info"
                  subtitle={!isMobile ? t("Matches Played") : undefined}
                />
              </Grid>
            </Grid>

            {/* Enhanced Rating Circle */}
            {totalGames > 0 && (
              <Zoom in={showStats} timeout={800}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 2, sm: 3 } }}>
                  <Card
                    key={animationKey}
                    elevation={0}
                    sx={{
                      borderRadius: "50%",
                      width: { xs: 140, sm: 180 },
                      height: { xs: 140, sm: 180 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #e0e0e0, #f5f5f5)",
                      position: "relative",
                      animation: `${winRatePulse} 2s ease-in-out infinite, ${winRateProgress(winPercentage)} ${animationDuration}s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: { xs: "10px", sm: "12px" },
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        boxShadow: "inset 0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                      <Typography
                        variant={isMobile ? "h4" : "h3"}
                        sx={{
                          fontWeight: 900,
                          color: theme.palette.success.main,
                          background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {winRate}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: { xs: "0.8rem", sm: "1rem" },
                        }}
                      >
                        {t("Rating")}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: { xs: "0.6rem", sm: "0.7rem" },
                        }}
                      >
                        {winPercentage.toFixed(1)}% {t("Win Rate")}
                      </Typography>
                    </Box>
                  </Card>
                </Box>
              </Zoom>
            )}

            {/* Session Info */}
            {isMyUser && (
              <Fade in={showStats} timeout={1000}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    borderRadius: { xs: 2, sm: 3 },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
                      <Avatar
                        sx={{
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                        }}
                      >
                        <CompareArrowsIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                      </Avatar>
                      <Box>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: "0.9rem", sm: "1rem" }
                          }}
                        >
                          {t("Current Session")}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="textSecondary"
                          sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                        >
                          {avatar?.activeSessionStr?.split("-")[0] || t("No active session")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            )}
          </Box>
        ) : (
          // AI Player Section
          <Zoom in={showStats} timeout={600}>
            <Box sx={{ textAlign: "center", py: { xs: 3, sm: 4 } }}>
              <Avatar
                sx={{
                  width: { xs: 80, sm: 120 },
                  height: { xs: 80, sm: 120 },
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                  margin: "0 auto 2rem",
                  fontSize: { xs: "2rem", sm: "3rem" },
                }}
              >
                🤖
              </Avatar>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {t("AI Player")}
              </Typography>
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                color="textSecondary"
              >
                {t("Artificial Intelligence Opponent")}
              </Typography>
              <Chip
                icon={<PsychologyIcon />}
                label={t("Advanced AI")}
                sx={{ 
                  mt: 2,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  height: { xs: 28, sm: 32 }
                }}
                color="secondary"
              />
            </Box>
          </Zoom>
        )}
      </DialogContent>

      {/* Enhanced Actions */}
      <DialogActions sx={{ 
        p: { xs: 2, sm: 3 }, 
        gap: { xs: 1, sm: 2 }, 
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        flexDirection: { xs: "column", sm: "row" }
      }}>
        {!isMyUser && avatar?.userType !== "AI" && (
          <Button
            onClick={handleAddOrRemove}
            startIcon={<FavoriteIcon />}
            variant="contained"
            fullWidth={isMobile}
            sx={{
              flex: { xs: "none", sm: 1 },
              py: { xs: 1.2, sm: 1.5 },
              borderRadius: { xs: 2, sm: 3 },
              fontWeight: 700,
              fontSize: { xs: "0.9rem", sm: "1rem" },
              background: userData?.favorites?.some(fav => fav === avatar?.id)
                ? "linear-gradient(135deg, #f44336, #d32f2f)"
                : "linear-gradient(135deg, #4a707d, #37474f)",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
          >
            {userData?.favorites?.some(fav => fav === avatar?.id)
              ? t("Remove from Favorites")
              : t("Add to Favorites")
            }
          </Button>
        )}

        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            minWidth: { xs: "auto", sm: 120 },
            py: { xs: 1.2, sm: 1.5 },
            borderRadius: { xs: 2, sm: 3 },
            fontWeight: 700,
            fontSize: { xs: "0.9rem", sm: "1rem" },
            borderColor: alpha(theme.palette.text.primary, 0.3),
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              transform: "scale(1.02)",
            },
          }}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Profile;